import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';

export const ApiConstants = {
  // NODE_LIST: '/lookup/nodes/fetch-for-select',
  NODE_LIST: '/express-delivery/origins',
  DESTINATIONS: '/express-delivery/routes/destinations',
  VALIDATION: '/express-delivery/routes/validate',
} as const;

export interface AvailabilityRequest {
  originNodeCode: string;
  destinationNodeCode: string;
}

export interface AvailabilityResponse {
  valid: boolean;
  matchedRouteGroup: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface PaxiNode {
  nodeIdNo: number;
  nodeCode: string;
  nodeShortName: string;
}

export interface PaxiDestinationNode {
  node_id_no: number;
  node_code: string;
  node_short_name: string;
}

export interface DestinationResponse {
  originNodeCode: string;
  destinations: PaxiNode[];
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  private readonly fallbackFaqs: FaqItem[] = [
    {
      question: 'How fast is PAXI Express delivery?',
      answer: 'PAXI Express is designed for next-business-day delivery between eligible stores.',
    },
    {
      question: 'How do I check route eligibility?',
      answer: 'Choose a sending and collection store on the landing page, then select CHECK AVAILABILITY.',
    },
    {
      question: 'What if my route is not available?',
      answer: 'You can still send via the standard PAXI route. Express is only available on selected routes.',
    },
  ];

  getFaqs(): Observable<FaqItem[]> {
    if (!this.apiBaseUrl) {
      return of(this.fallbackFaqs);
    }

    return this.http
      .get<FaqItem[]>(`${this.apiBaseUrl}/faqs`)
      .pipe(catchError(() => of(this.fallbackFaqs)));
  }

  getNodeList(): Observable<PaxiNode[]> {
    return this.http.get<PaxiNode[]>(ApiConstants.NODE_LIST, {
      headers: { 'x-api-key': environment.apiKey },
      params: {
        province: ['Gauteng', 'North West'],
      },
    });
  }

  getDestinations(originNodeCode: string): Observable<DestinationResponse> {
    return this.http.get<{
      originNodeCode: string;
      destinations: PaxiDestinationNode[];
    }>(ApiConstants.DESTINATIONS, {
      headers: { 'x-api-key': environment.apiKey,
        // 'x-target': 'localhost'
       },
      params: { originNodeCode },
    }).pipe(
      map((response) => ({
        originNodeCode: response.originNodeCode,
        destinations: response.destinations.map((dest) => ({
          nodeIdNo: dest.node_id_no,
          nodeCode: dest.node_code,
          nodeShortName: dest.node_short_name,
        })),
      }))
    );
  }

  checkAvailability(payload: AvailabilityRequest): Observable<AvailabilityResponse> {
    return this.http
      .post<AvailabilityResponse>(ApiConstants.VALIDATION, payload, { headers: { 'x-api-key': environment.apiKey } })
      // .pipe(
      //   catchError(() =>
      //     of({
      //       available: false,
      //       message: 'Unable to check availability right now. Please try again shortly.',
      //     }),
      //   ),
      // );
  }
}
